
import React, { useState, useEffect } from 'react'
import { Grid, Typography } from '@mui/material'
import { withComponentContext } from 'widgets-shared/core'
import { compose } from 'widgets-shared/core'
import GSSVG from 'smartstream-widgets/controls/GSSVG'
import LogoFlag
  from 'smartstream-widgets/controls/LogoFlag/LogoFlag'
import MarketSelection
  from 'smartstream-widgets/controls/MarketSelection/MarketSelection'

import { PositionState, RowProps } from './types'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

const styles = (theme: Theme) =>  createStyles({
  captionOdd: {
    fontSize: 10
  },
  competitionName: {
    color: theme.palette.grey['A400'],
    fontFamily: `${theme.typography['variantFontFamily']},${theme.typography['fontFamily']}`,
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: 1.15,
    marginRight: 6,
    minWidth: '7ch'
  },
  competitorName: {
    color: theme.palette.grey['A400'],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    lineHeight: 1.15,
    flex: 1,
    fontSize: 12,
    fontWeight: 'bold',
    height: 'calc(100% - 10px)',
    maxHeight: 36,
    minHeight: 28,
    minWidth: 0
  },
  logoFlag: {
    alignSelf: 'stretch'
  },
  logoImg: {
    alignSelf: 'stretch',
    maxWidth: 32,
    height: 0,
    minHeight: 28,
    objectFit: 'contain',
    objectPosition: 'center bottom'
  },
  logoFlagWrapper: {
    height: '100%',
    width: 36
  },
  marketSelection: {
    height: 'calc(100% - 16px)',
    margin: `0px 5px`,
    maxHeight: 30,
    minHeight: 20,
    position: 'static',
    '&::before': {
      content: '""',
      display: 'block',
      height: '100%',
      left: 0,
      position: 'absolute',
      top: 0,
      width: '100%'
    }
  },
  marketSelectionDisabled: {},
  overflowEllipsis: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  position: {
    alignSelf: 'stretch',
    alignItems: 'center',
    color: theme.palette.primary.main,
    display: 'flex',
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: 1.15,
    minWidth: '2ch',
    padding: `5px 4px 5px 12px`,
    position: 'relative',
    transition: 'color 200ms',
  },
  positionArrow: {
    color: 'transparent',
    position: 'absolute',
    height: 20,
    left: 0,
    top: '50%',
    transform: 'translateY(-50%) rotate(90deg)',
    transition: 'color 150ms, transform 0s 200ms',
    width: 12
  },
  positionChangeHigher: {
    '&, & $positionArrow': {
      color: theme.palette.success.main,
    },
    '& $positionArrow': {
      transform: 'translateY(-50%) rotate(0deg)',
      transition: 'color 150ms, transform 300ms 0s',
    }
  },
  positionChangeLower: {
    '&, & $positionArrow': {
      color: theme.palette.error.main,
    },
    '& $positionArrow': {
      transform: 'translateY(-50%) rotate(180deg)',
      transition: 'color 150ms, transform 300ms 0s',
    }
  },
  root: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    flex: 1,
    minHeight: 31,
    position: 'relative',
    transition: 'background-color 250ms ease-in-out',
    transform: 'translate3d(0,0,0)',
    '&:first-child': {
      borderTop: `1px solid ${theme.palette.divider}`
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected
    }
  },
  subtitle: {
    fontSize: 9,
    fontStyle: 'italic',
    fontWeight: 'normal'
  },
  competitorFlagSize: {
    height: '100%',
  },
  competitorFlagSizeDual:{
    width: 36,
    height: '100%',
  },
  competitorLogoSize: {

  },
  competitorLogoSizeDual:{
    width: 18,
    height: 18,
    padding: Math.round(18 * 0.125)
  }
})

export function CompetitorMarketRow (props: RowProps) {
  const {
    classes,
    competitionName,
    competitorsBrand,
    config: { BASE_ASSET_PATH, showLogos = true },
    logoMode = 'logoFlag',
    market: {
      selection,
      sourceId
    },
    name,
    position,
    subtitle,
    updateAnimationDuration = 30000,
    id
  } = props

  const [positionState, setPositionState] = useState({
    position,
    change: 0
  } as PositionState)

  const isDualCompetitor = competitorsBrand && competitorsBrand.length === 2

  useEffect(() => {
    let timeoutId
    const clearChange = (prevState: PositionState) => ({
      change: 0,
      position: prevState.position,
    })
    if (positionState.position !== position) {
      setPositionState((prevState) => ({
        change: prevState.position - position,
        position: position,
      }))
      timeoutId = setTimeout(() => {
        setPositionState(clearChange)
      }, updateAnimationDuration)
    }
    return () => {
      clearTimeout(timeoutId)
      setPositionState(clearChange)
    }
  }, [position])

  let positionClass = ''
  if(positionState.change < 0) positionClass = classes.positionChangeLower
  if(positionState.change > 0) positionClass = classes.positionChangeHigher

  return (
    <Grid
      container
      alignItems='center'
      className={classes.root}
      wrap='nowrap'
      data-testid='competitor-market-row'
    >
      <Grid container justifyContent='center' className={classes.logoFlagWrapper}>
        {
          logoMode === 'logoFlag' &&
          <LogoFlag
            className={classes.logoFlag}
            classes={{
              logoFlagSize: isDualCompetitor ? classes.competitorFlagSizeDual : classes.competitorFlagSize,
              logoSize: isDualCompetitor ? classes.competitorLogoSizeDual : classes.competitorLogoSize
            }}
            shape='diagonal'
            competitorsBrand={competitorsBrand}
            svgKey={id}
          />
        }
        {
          logoMode === 'logo' && showLogos &&
          <img
            className={classes.logoImg}
            src={(competitorsBrand && competitorsBrand.length > 0 && (competitorsBrand[0].logo.svg || competitorsBrand[0].logo.x1)) || `${BASE_ASSET_PATH}/img/defaultUserLogo.svg`}
          />
        }
      </Grid>
      <Typography className={`${classes.position} ${positionClass}`} component='span' data-testid='position-indicator'>
        <GSSVG
          className={classes.positionArrow}
          xlinkHref={`${BASE_ASSET_PATH}/img/icons.svg#ic-price-change-arrow`}
        />
        {position}
      </Typography>
      {
        competitionName &&
        <Typography className={classes.competitionName} component='span' data-testid='competition-name'>
          {competitionName}
        </Typography>
      }
      <Typography className={classes.competitorName} component='h3'>
        <span className={classes.overflowEllipsis}>{name || selection.name}</span>
        {
          subtitle &&
          <span className={`${classes.subtitle} ${classes.overflowEllipsis}`} data-testid='subtitle'>
            {subtitle}
          </span>
        }
      </Typography>
      <MarketSelection
        classes={{
          oddButton: classes.marketSelection,
          captionOdd: classes.captionOdd,
          buttonDisabled: classes.marketSelectionDisabled
        }}
        selection={selection}
        sourceId={sourceId}
      />
    </Grid>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'CompetitorMarketRow' , defaultTheme: getDefaultTheme})
)(CompetitorMarketRow)
